/**
 * Back-office shell -- Design System v1.0, section 06.
 *
 * Fixed Espresso sidebar, 240 px. Active entry on Sable clair, a counter pill
 * for the orders waiting in NEW. The restaurant and sign-out stay anchored at
 * the bottom. Below 1024 px the sidebar becomes a drawer opened from the top
 * bar.
 *
 * Every authenticated page renders inside this layout, through <Outlet />.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import type { OrderBoardResponse, UserRole } from '@scan2eat/shared';

import { apiFetch } from '../api/client.js';
import { useAuth } from '../auth/context.js';
import { subscribeBoardCounts } from '../lib/board-counts.js';
import { applyFavicon } from '../lib/favicon.js';
import { initials } from '../lib/initials.js';
import { getAdminSocket, type AdminSocket } from '../realtime/socket.js';
import { labels } from '../labels.js';
import { DropdownItem, DropdownMenu } from './DropdownMenu.js';
import { Icon, type IconName } from './Icon.js';
import { Logo } from './Logo.js';
import { NotificationBell } from './NotificationBell.js';
import { ThemeToggle } from './ThemeToggle.js';
import styles from './AdminLayout.module.css';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  min?: UserRole;
}

const NAV: NavItem[] = [
  { to: '/', label: labels.nav.board, icon: 'board' },
  { to: '/historique', label: labels.nav.history, icon: 'history' },
  { to: '/menu', label: labels.nav.menu, icon: 'menu' },
  { to: '/tables', label: labels.nav.tables, icon: 'qr' },
  { to: '/bilan', label: labels.nav.report, icon: 'chart', min: 'MANAGER' },
  { to: '/equipe', label: labels.nav.team, icon: 'users', min: 'OWNER' },
  { to: '/solde', label: labels.nav.balance, icon: 'cash', min: 'OWNER' },
];

const RANK: Record<UserRole, number> = { PLATFORM_ADMIN: 4, OWNER: 3, MANAGER: 2, STAFF: 1 };

export function AdminLayout() {
  const { auth, logout } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pending = usePendingCount(Boolean(auth?.restaurant), location.pathname === '/');

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // L'icone d'onglet suit le restaurant courant : utile a un administrateur
  // de plateforme qui bascule entre plusieurs restaurants dans le meme
  // navigateur. Sans restaurant (PLATFORM_ADMIN non bascule) ou sans logo,
  // retombe sur l'icone Scan2Eat par defaut.
  useEffect(() => {
    applyFavicon(auth?.restaurant?.logoUrl);
  }, [auth?.restaurant?.logoUrl]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  if (!auth) return null;

  const role = auth.user.role;
  const rank = RANK[role] ?? 0;
  const items = NAV.filter((item) => !item.min || rank >= RANK[item.min]);

  return (
    <div className={styles.shell}>
      <a href="#contenu" className={styles.skip}>{labels.nav.skipToContent}</a>

      <aside
        className={`${styles.sidebar} ${drawerOpen ? styles.sidebarOpen : ''}`}
        aria-label={labels.nav.label}
      >
        <div className={styles.brand}>
          <Link to="/" className={styles.brandLink} aria-label={labels.app.name}>
            <Logo size={32} withWordmark tone="dark" />
          </Link>
          <button
            type="button"
            className={`btn-icon btn-icon--quiet ${styles.closeDrawer}`}
            onClick={() => setDrawerOpen(false)}
            aria-label={labels.nav.closeMenu}
          >
            <Icon name="close" />
          </button>
        </div>


        <nav className={styles.nav}>
          <p className={styles.navHeading}>{labels.nav.heading}</p>
          <ul className={styles.navList}>
            {items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => `${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
                >
                  <Icon name={item.icon} />
                  <span className={styles.navLabel}>{item.label}</span>
                  {item.to === '/' && pending > 0 && (
                    <span className={styles.navCount} aria-label={labels.nav.pendingCount(pending)}>
                      {pending > 99 ? '99+' : pending}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.venue}>
            <span className={styles.venueMark} aria-hidden="true">
              <Icon name="store" size={18} />
            </span>
            <span className={styles.venueText}>
              <strong className="truncate">{auth.restaurant?.name ?? labels.profile.noRestaurant}</strong>
              <span>{labels.team.roleLabels[role] ?? role}</span>
            </span>
          </div>
          <button type="button" className={styles.signOut} onClick={() => void logout()}>
            <Icon name="logout" />
            {labels.nav.logout}
          </button>
        </div>
      </aside>

      {drawerOpen && (
        <button
          type="button"
          className={styles.scrim}
          aria-label={labels.nav.closeMenu}
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <div className={styles.column}>
        <header className={styles.topbar}>
          <button
            type="button"
            className={`btn-icon btn-icon--quiet ${styles.menuButton}`}
            onClick={() => setDrawerOpen(true)}
            aria-label={labels.nav.openMenu}
            aria-expanded={drawerOpen}
          >
            <Icon name="list" />
          </button>
          <div className={styles.context}>
            <span className={styles.contextName}>{auth.restaurant?.name ?? labels.app.name}</span>
            <span className={styles.contextDate}>{todayLabel()}</span>
          </div>
          <div className={styles.tools}>
            <NotificationBell />
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>

        {/* A password handed over by voice when the restaurant opened must not
            live forever. The bar goes away once the owner picks their own.
            See docs/adr/0014. */}
        {auth.user.mustChangePassword && (
          <div role="alert" className={styles.provisional}>
            <Icon name="lock" size={18} />
            <span>{labels.mustChangePassword.banner}</span>
            <Link to="/profil">{labels.mustChangePassword.action}</Link>
          </div>
        )}

        <main id="contenu" className={styles.main} tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/* -- User menu -- */

function UserMenu() {
  const { auth, logout } = useAuth();
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  if (!auth) return null;
  const { user } = auth;

  return (
    <div className={styles.userWrap}>
      <button
        ref={anchor}
        type="button"
        className={styles.userButton}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <span className="avatar">
          {user.photoUrl ? <img src={user.photoUrl} alt="" /> : initials(user.firstName, user.lastName)}
        </span>
        <span className={styles.userText}>
          <strong>{user.firstName} {user.lastName.charAt(0)}.</strong>
          <span>{labels.team.roleLabels[user.role] ?? user.role}</span>
        </span>
        <Icon name="chevron-down" size={16} className={styles.userChevron} />
      </button>
      <DropdownMenu open={open} onClose={close} anchorRef={anchor}>
        <div className={`dropdown-label ${styles.menuIdentity}`}>
          <strong>{user.firstName} {user.lastName}</strong>
          {user.email}
        </div>
        <div className="dropdown-separator" />
        <DropdownItem to="/profil" onClick={close} icon={<Icon name="user" size={18} />}>
          {labels.nav.profile}
        </DropdownItem>
        <DropdownItem onClick={() => { close(); void logout(); }} icon={<Icon name="logout" size={18} />} danger>
          {labels.nav.logout}
        </DropdownItem>
      </DropdownMenu>
    </div>
  );
}


/* -- Pending counter -- */

/**
 * Orders waiting in NEW, for the sidebar pill.
 *
 * On the board the page publishes its counts; elsewhere the layout reads the
 * board once, then again on each order event and every thirty seconds. The
 * layout never opens the socket itself: the page on screen owns it.
 */
function usePendingCount(enabled: boolean, onBoard: boolean): number {
  const [count, setCount] = useState(0);

  useEffect(() => subscribeBoardCounts((counts) => setCount(counts.NEW)), []);

  useEffect(() => {
    if (!enabled || onBoard) return;
    let attached: AdminSocket | null = null;
    let timer: number | undefined;

    const fetchCounts = () => {
      apiFetch<OrderBoardResponse>('/admin/orders/board')
        .then((board) => setCount(board.counts.NEW))
        .catch(() => undefined);
    };
    const onEvent = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(fetchCounts, 800);
    };
    const sync = () => {
      const current = getAdminSocket();
      if (current !== attached) {
        attached?.off('order:created', onEvent as never);
        attached?.off('order:status_changed', onEvent as never);
        current?.on('order:created', onEvent as never);
        current?.on('order:status_changed', onEvent as never);
        attached = current;
      }
    };

    fetchCounts();
    sync();
    const interval = window.setInterval(() => { sync(); fetchCounts(); }, 30_000);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timer);
      attached?.off('order:created', onEvent as never);
      attached?.off('order:status_changed', onEvent as never);
    };
  }, [enabled, onBoard]);

  return enabled ? count : 0;
}

function todayLabel(): string {
  const text = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  return text.charAt(0).toUpperCase() + text.slice(1);
}
